// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { WebStorageStateStore, UserManager } from 'oidc-client-ts';
import { PropsWithChildren } from 'react';
import { AuthProvider as OidcAuthProvider } from 'react-oidc-context';
import Loading from '../components/Loading';
import { useIdpInfo } from '../contexts/IdpContext';

const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { idpInfo } = useIdpInfo();

  if (!idpInfo) {
    return <Loading />;
  }

  if (idpInfo.algorithm === 'hs-256-unsafe' || idpInfo.algorithm === 'idp-not-set') {
    return children;
  }

  // create our own user manager so we can add a userManager.events.addUserLoaded event.
  // unfortunately the normal signinRedirectCallback does not wait to be finished before
  // considering itself authenticated - and we need to wait until ensureUser completes
  // before making any requests
  const userManager = new UserManager({
    automaticSilentRenew: true,
    authority: idpInfo.authority,
    client_id: idpInfo.clientId,
    redirect_uri: idpInfo.redirectUri,
    scope: idpInfo.scope,
    response_type: 'code',
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    extraQueryParams: { audience: idpInfo.audience },
  });

  userManager.events.addUserLoaded(() => {
    window.history.replaceState({}, document.title, window.location.pathname);
  });

  return <OidcAuthProvider userManager={userManager}>{children}</OidcAuthProvider>;
};

export default AuthProvider;
